import React, { useState, useEffect } from "react";
import { globalAlert, globalLoader } from "../actions/commonActions";
import { getRequest, postRequest } from "../actionTypes/axiosUtility";
import { GLOBAL_API } from "../actionTypes/types";
import { useTranslation } from "react-i18next";

const TranslationConfig = () => {
  const { t } = useTranslation();
  const [multiLangEnabled, setMultiLangEnabled] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [enabledLanguages, setEnabledLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState("en");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      globalLoader(true);

      // Fetch the master list of supported languages
      const supportedRes = await getRequest(
        `${GLOBAL_API}/translations/public/v1/supported-languages`
      );
      if (
        supportedRes?.data?.status === 200 &&
        supportedRes?.data?.data?.languages
      ) {
        setSupportedLanguages(supportedRes.data.data.languages);
      }

      // Fetch current org translation settings
      const configRes = await getRequest(
        `${GLOBAL_API}/translations/admin/v1/translations/languages`
      );
      if (configRes?.data?.status === 200 && configRes?.data?.data) {
        const cfg = configRes.data.data;
        setMultiLangEnabled(cfg.enabled || false);
        const langs = cfg.enabledLanguages || [];
        setEnabledLanguages(langs.map((l) => (typeof l === "string" ? l : l.code)));
        setDefaultLanguage(cfg.defaultLanguage || "en");
      }

      setLoaded(true);
      globalLoader(false);
    } catch (err) {
      globalLoader(false);
      setLoaded(true);
    }
  };

  const handleToggleLanguage = (code) => {
    setEnabledLanguages((prev) => {
      if (prev.includes(code)) {
        // Don't allow removing the default language
        if (code === defaultLanguage) {
          globalAlert(
            "error",
            t("admin.translationConfig.cannotDisableDefault")
          );
          return prev;
        }
        return prev.filter((c) => c !== code);
      } else {
        return [...prev, code];
      }
    });
  };

  const handleDefaultLanguageChange = (e) => {
    const lang = e.target.value;
    setDefaultLanguage(lang);
    // Ensure the default language is also enabled
    if (!enabledLanguages.includes(lang)) {
      setEnabledLanguages((prev) => [...prev, lang]);
    }
  };

  const handleSave = async () => {
    if (enabledLanguages.length === 0) {
      globalAlert("error", t("admin.translationConfig.atLeastOneLanguage"));
      return;
    }
    if (!enabledLanguages.includes(defaultLanguage)) {
      globalAlert("error", t("admin.translationConfig.defaultMustBeEnabled"));
      return;
    }

    try {
      globalLoader(true);
      const res = await postRequest(
        `${GLOBAL_API}/translations/admin/v1/translations/languages`,
        {
          enabled: multiLangEnabled,
          enabledLanguages: enabledLanguages.map((code) => {
            const lang = supportedLanguages.find((l) => l.code === code);
            return { code, name: lang ? lang.name : code };
          }),
          defaultLanguage: defaultLanguage,
        }
      );
      if (res?.data?.status === 200) {
        globalAlert("success", res?.data?.message || "Translation settings saved.");
      } else {
        globalAlert("error", res?.data?.message || "Failed to save settings.");
      }
      globalLoader(false);
    } catch (err) {
      globalAlert(
        "error",
        err?.response?.data?.message || t("admin.translationConfig.failedToSave")
      );
      globalLoader(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="row">
      <div className="col-12">
        {/* Multi-language toggle */}
        <div className="form-own-group">
          <label className="form-own-label">{t("admin.translationConfig.enableMultiLanguage")}</label>
          <div>
            <input
              type="checkbox"
              id="multiLangYes"
              checked={multiLangEnabled === true}
              onChange={() => setMultiLangEnabled(true)}
              style={{ height: "16px", width: "16px" }}
            />
            <label
              htmlFor="multiLangYes"
              className="form-own-label pl-2 position-relative"
              style={{ top: "-3px", marginLeft: "8px", marginRight: "20px" }}
            >
              {t("common.yes")}
            </label>
            <input
              type="checkbox"
              id="multiLangNo"
              checked={multiLangEnabled === false}
              onChange={() => setMultiLangEnabled(false)}
              style={{ height: "16px", width: "16px" }}
            />
            <label
              htmlFor="multiLangNo"
              className="form-own-label pl-2 position-relative"
              style={{ top: "-3px", marginLeft: "8px" }}
            >
              {t("common.no")}
            </label>
          </div>
        </div>

        {multiLangEnabled && (
          <>
            {/* Enabled Languages */}
            <div className="form-own-group mt-4">
              <label className="form-own-label">{t("admin.translationConfig.enabledLanguages")}</label>
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "12px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {supportedLanguages.length > 0 ? (
                  supportedLanguages.map((lang) => (
                    <div
                      key={lang.code}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 0",
                      }}
                    >
                      <input
                        type="checkbox"
                        id={`lang-${lang.code}`}
                        checked={enabledLanguages.includes(lang.code)}
                        onChange={() => handleToggleLanguage(lang.code)}
                        style={{ height: "16px", width: "16px" }}
                      />
                      <label
                        htmlFor={`lang-${lang.code}`}
                        className="form-own-label pl-2 mb-0"
                        style={{ cursor: "pointer", marginLeft: "10px" }}
                      >
                        {lang.name} ({lang.code})
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="text-muted">{t("admin.translationConfig.noLanguagesAvailable")}</div>
                )}
              </div>
            </div>

            {/* Default Language */}
            <div className="form-own-group mt-4">
              <label className="form-own-label">{t("admin.translationConfig.defaultLanguage")}</label>
              <select
                className="form-control"
                value={defaultLanguage}
                onChange={handleDefaultLanguageChange}
                style={{ maxWidth: "300px" }}
              >
                {enabledLanguages.map((code) => {
                  const lang = supportedLanguages.find((l) => l.code === code);
                  return (
                    <option key={code} value={code}>
                      {lang ? `${lang.name} (${code})` : code}
                    </option>
                  );
                })}
              </select>
            </div>
          </>
        )}
      </div>

      <div className="col-12 mt-4">
        <div className="admin-btn">
          <button
            type="button"
            className="btn primary-btn ml-2"
            onClick={handleSave}
          >
            {t("admin.translationConfig.saveSettings")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TranslationConfig;
